<script setup lang="ts">
import type { KjdsDetailPageProps } from '@/types/appTypes'

const props = withDefaults(defineProps<KjdsDetailPageProps>(), {})

const publicRuntimeConfig = useRuntimeConfig().public
const app = useNuxtApp()
const route = useRoute()
const router = useRouter()
const storeIndex = useStoreIndex()
const storeUser = useStoreUser()

if (import.meta.client) {
  const storeUser = useStoreUser()
  if (!storeUser.state.isLogin) {
    storeUser.setLoginRedirectPath(route.fullPath)
    router.replace('/login')
  }
}

const pageData = {
  item: props.kjdsDetailData.article as Article,
  tEyeCatch: null, // アイキャッチ情報
  sVenue: null, // 会場
  sMuseumArticleSlug: null, // ミュージアムスラッグ
  sPeriodLabel: null, // 会期
  sAddress: null, // 所在地
  sTell: null, // TEL
  sOfficialUrl: null, // 公式サイト
  sEventArticleSlug: null, // 展覧会スラッグ
  sBody: null, // ボディ
  description: '',
  isFormBefore: false,
  isFormAfter: false,
}

const setHead = () => {
  const headTitle = props.kjdsDetailData.article.title + 'のご招待券プレゼント'
  // Nuxt2と同じ説明文を設定
  let headDescription = props.kjdsDetailData.article.title
    + 'の招待券プレゼントページ。アイエムがおすすめする'
    + props.kjdsDetailData.article.title
    + 'のチケットを、今だけプレゼント中。ウェブからカンタン応募、美術館や博物館に行きたいなら、アイエムで！'
  if (props.kjdsDetailData.article.description?.length) {
    headDescription = props.kjdsDetailData.article.description
  }
  // ぱんくず作成
  storeIndex.setBreadCrumbs([
    {
      text: 'ご招待券プレゼント',
      disabled: false,
      to: '/present',
    },
    {
      text: props.kjdsDetailData.article.title,
      disabled: true,
    },
  ])

  const headInput = app.$makeMeta({
    title: headTitle,
    description: headDescription,
  })

  useHead(headInput)

  // scriptは別のuseHead呼び出しに分ける（SSR時にメタタグが正しく生成されるように）
  // Nuxt2と同じ動作にするため、body: trueを設定（scriptをbodyタグ内に追加）
  useHead({
    script: [
      {
        body: true,
        innerHTML: `
          var load = 0;
          var gid = document.getElementById('my-google-form')
          if (gid) {
            gid.onload = function () {
              // Execute on every reload on iFrame
              load++;
              if (load > 1) {
                // Second reload is a submit
                document.location = "${publicRuntimeConfig.baseOrigin}/static/present-complete";
              }
            }
          }
        `,
      } as any,
    ],
  })
}

const checkUserDetail = () => {
  if (storeUser.state.userInfo.custom
    && storeUser.state.userInfo.custom.f_name
    && storeUser.state.userInfo.custom.l_name
    && storeUser.state.userInfo.custom.postal_code
    && storeUser.state.userInfo.custom.tel) {
    return true
  }
  return false
}

const checkAgeLimit = () => {
  if (storeUser.state.userInfo.custom && storeUser.state.userInfo.custom.birthday) {
    const year = useDayjs().diff(storeUser.state.userInfo.custom.birthday, 'year')
    if (year >= 16) {
      return false
    }
  }
  return true
}

const onClickAccount = () => {
  navigateTo({
    name: 'mypage-account',
    query: { present: route.params.slug },
    hash: '#userinfo',
  })
}

const initPage = () => {
  setHead()

  // プレゼントから表示カラム作成
  if (!pageData.tEyeCatch) {
    pageData.tEyeCatch = app.$getEyeCatch(pageData.item)
  } // アイキャッチ情報
  if (!pageData.sBody) {
    pageData.sBody = app.$getArticleBody(pageData.item)
  } // ボディ

  // 関連記事からイベント取得
  const eventItem = app.$getArticleRelationsSingle(pageData.item, 'event', null)
  if (eventItem) {
    // イベントから表示カラム作成
    if (!pageData.sVenue) {
      pageData.sVenue = app.$getEventVenue(eventItem, false)
    } // 会場
    if (!pageData.sMuseumArticleSlug) {
      pageData.sMuseumArticleSlug = app.$getEventMuseumArticleSlug(eventItem)
    } // ミュージアムスラッグ
    if (!pageData.sAddress) {
      pageData.sAddress = app.$getEventAddress(eventItem, false)
    } // 住所
    if (!pageData.sPeriodLabel) {
      pageData.sPeriodLabel = app.$getEventPeriodLabel(eventItem)
    } // 開催中情報
    if (!pageData.sTell) {
      pageData.sTell = app.$getEventTell(eventItem)
    } // 電話番号
    if (!pageData.sOfficialUrl) {
      pageData.sOfficialUrl = app.$getPresentOfficialUrl(pageData.item, eventItem)
    } // 公式サイト
    if (!pageData.sEventArticleSlug) {
      pageData.sEventArticleSlug = eventItem.article_slug
    } // 展覧会スラッグ
  }

  //  フォームの期間
  if (pageData.item?.form_date_from && pageData.item?.form_date_to) {
    pageData.isFormBefore = useDayjs().isBefore(pageData.item.form_date_from, 'minute')
    pageData.isFormAfter = useDayjs().isAfter(pageData.item.form_date_to, 'minute')
    console.log('isFormAfter:', pageData.isFormBefore, pageData.isFormAfter)
  }
}
initPage()
</script>

<template>
  <div class="p-articleDetailPage">
    <ClientOnly>
      <div class="c-presentItem">
        <div class="c-presentItem_card_content">
          <div
            v-if="pageData.tEyeCatch"
            class="c-presentItem_card_content_imageBox"
          >
            <div>
              <CommonImage
                class="c-presentItem_card_content_imageBox_image"
                v-bind:src="pageData.tEyeCatch.oImg"
                error-src="/images/noimage.svg"
                object-fit="contain"
                object-position="top center"
              />
              <div
                v-if="pageData.tEyeCatch.sCaption"
                class="c-presentItem_card_content_imageBox_caption"
              >
                {{ pageData.tEyeCatch.sCaption }}
              </div>
            </div>
          </div>
          <div class="c-presentItem_card_content_detailBox">
            <div class="c-presentItem_card_content_detailBox_detail">
              <div
                v-if="pageData.item?.title"
                class="c-presentItem_card_content_detailBox_title"
              >
                {{ pageData.item.title }}
              </div>

              <div
                v-if="pageData.sMuseumArticleSlug && pageData.sVenue"
                class="c-presentItem_card_content_detailBox_detail_info"
              >
                <div class="c-presentItem_card_content_detailBox_detail_label">
                  会場
                </div>
                <div class="c-presentItem_card_content_detailBox_detail_venue">
                  <nuxt-link
                    v-bind:to="'/museum/' + pageData.sMuseumArticleSlug"
                  >
                    <div v-html="app.$trimEnter(pageData.sVenue)" />
                  </nuxt-link>
                </div>
              </div>

              <div
                v-if="pageData.sPeriodLabel"
                class="c-presentItem_card_content_detailBox_detail_info"
              >
                <div class="c-presentItem_card_content_detailBox_detail_label">
                  会期
                </div>
                <div
                  class="c-presentItem_card_content_detailBox_detail_time"
                >
                  {{ pageData.sPeriodLabel }}
                </div>
              </div>
              <div
                v-if="pageData.sAddress"
                class="c-presentItem_card_content_detailBox_detail_info"
              >
                <div class="c-presentItem_card_content_detailBox_detail_label">
                  所在地
                </div>
                <div
                  class="c-presentItem_card_content_detailBox_detail_location"
                >
                  <div v-html="pageData.sAddress" />
                </div>
              </div>
              <div
                v-if="pageData.sTell"
                class="c-presentItem_card_content_detailBox_detail_info"
              >
                <div class="c-presentItem_card_content_detailBox_detail_label">
                  TEL
                </div>
                <div
                  class="c-presentItem_card_content_detailBox_detail_tell"
                >
                  {{ pageData.sTell }}
                </div>
              </div>
              <div
                v-if="pageData.sOfficialUrl"
                class="c-presentItem_card_content_detailBox_detail_info"
              >
                <div class="c-presentItem_card_content_detailBox_detail_label">
                  公式サイト
                </div>
                <a
                  v-bind:href="pageData.sOfficialUrl"
                  class="c-presentItem_link"
                  target="_blank"
                >
                  {{ pageData.sOfficialUrl }}
                </a>
              </div>
              <div
                v-if="pageData.item?.form_date_to"
                class="c-presentItem_card_content_detailBox_detail_info"
              >
                <div class="c-presentItem_card_content_detailBox_detail_label">
                  締切
                </div>
                <div class="c-presentItem_card_content_detailBox_detail_deadline">
                  {{ app.$getPeriodDayLabel(pageData.item.form_date_to) }}
                </div>
              </div>

              <div
                v-if="pageData.item?.custom?.capacity"
                class="c-presentItem_card_content_detailBox_detail_info"
              >
                <div class="c-presentItem_card_content_detailBox_detail_label">
                  定員
                </div>
                <div class="c-presentItem_card_content_detailBox_detail_tell">
                  {{ pageData.item.custom.capacity }}
                </div>
              </div>
            </div>

            <div
              v-if="pageData.sEventArticleSlug"
              class="c-presentItem_card_content_detailBox_link"
            >
              <nuxt-link
                class="c-presentItem_card_content_detailBox_link_button"
                v-bind:to="`/event/${pageData.sEventArticleSlug}`"
              >
                <div class="c-presentItem_card_content_detailBox_link_button_text">
                  展覧会詳細をみる
                </div>
              </nuxt-link>
            </div>
          </div>
        </div>
        <div
          v-if="pageData.sBody"
          class="c-presentItem_contentDetail"
        >
          <div
            class="c-presentItem_contentDetail_text l-blank-icon-none"
            v-html="pageData.sBody"
          />
        </div>

        <template
          v-if="pageData.isFormBefore"
        >
          <div
            class="c-presentItem_contentNote"
          >
            プレゼントの応募期間外です。
          </div>
        </template>
        <template
          v-else-if="pageData.isFormAfter"
        >
          <div
            class="c-presentItem_contentNote"
          >
            プレゼントの応募期間は終了しました。
          </div>
        </template>
        <template
          v-else-if="!checkUserDetail()"
        >
          <div
            class="c-presentItem_userInfoReq"
          >
            <div
              class="c-presentItem_contentNote"
            >
              プレゼントの応募には、お名前・ご住所などを会員情報に登録する必要があります。
              <br>以下のページから登録をお願いいたします。
            </div>
            <div
              class="c-presentItem_accountReturn"
              v-on:click="onClickAccount"
            >
              アカウント情報変更ページへ
            </div>
          </div>
        </template>
        <template v-else>
          <FormJson
            v-bind:slug="pageData.item.article_slug"
            v-bind:article-type-slug="pageData.item.article_type_slug"
            v-bind:format-string="pageData.item.post_form_json"
            v-bind:age-limit="checkAgeLimit()"
          />
          <div
            class="c-presentItem_contentNote"
          >
            プレゼントの当選者は厳正なる抽選の上で決定し、<br>発表はプレゼントの発送をもって代えさせていただいております。<br>当選者には締切から最長でも5日以内には届くようにお送りいたします。
          </div>
        </template>
        <RelationBox
          article="report"
          v-bind:header="'関連するレポート'"
          v-bind:relations="pageData.item.relations"
          v-bind:objects="props.kjdsDetailData.objects"
        />
      </div>
    </ClientOnly>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive" as responsive;

.c-presentItem {
  &_card {
    display: flex;
    flex-direction: column;
    width: 100%;

    &_content {
      display: flex;
      margin-bottom: 14px;
      padding-top: 20px;
      font-family: var(--g_font_family_NotoSansCJKjp);

      @include responsive.mq_down(sp) {
        display: block;
        margin-bottom: 30px;
      }

      &_imageBox {
        width: 170px;

        @include responsive.mq_down(sp) {
          width: 100%;
        }

        &_image {
          flex-shrink: 0;
          width: 170px;
          max-height: 230px;

          @include responsive.mq_down(sp) {
            width: 100%;
            max-height: none;
          }
        }

        &_caption {
          margin-top: 5px;
          color: var(--color_text_base);
          font-family: var(--g_font_family_NotoSansCJKjp);
          font-size: 12px;
          line-height: 2;
        }
      }

      &_detailBox {
        width: calc(100% - 197px);
        margin-top: -8px;
        margin-left: 27px;
        font-family: var(--g_font_family_NotoSansCJKjp);

        @include responsive.mq_down(sp) {
          width: 100%;
          margin: 10px 0 0;
        }

        &_title {
          color: var(--color_text_base);
          font-family: var(--g_font_family_NotoSansCJKjp);
          font-size: 19px;
          font-weight: bold;

          @include responsive.mq_down(sp) {
            margin: 0 0 16px;
            font-size: 20px;
          }
        }

        &_detail {
          color: var(--color_text_base);
          font-size: 14px;
          line-height: 1.8;

          &_info {
            display: flex;
            margin-bottom: 1px;

            @include responsive.mq_down(sp) {
              display: flex;
            }
          }

          &_label {
            display: inline-block;
            min-width: 98px;
            color: var(--color_text_base);
            font-weight: bold;

            @include responsive.mq_down(sp) {
              flex-basis: 98px;
            }

            &::before {
              content: "";
              display: inline-block;
              position: relative;
              top: -2px;
              width: 8px;
              height: 8px;
              margin-right: 6px;
              background-color: var(--color_primary_link);
            }
          }

          &_venue {
            color: var(--color_primary_link);
          }

          &_deadline {
            color: var(--g_color_text_c20000);
            font-weight: bold;
          }
        }

        /*
        &_link {
          display: flex;
          justify-content: flex-start;
          margin-top: 10px;
          margin-bottom: 20px;

          @include responsive.mq_down(sp) {
            display: block;
            margin: 20px 0 40px;
          }

          & > button {
            @include responsive.mq_down(sp) {
              width: 100%;
              max-width: 80%;
              margin: 10px auto;
              font-size: 14px;
            }
          }

          &_button {
            display: flex;
            flex: 0 0 auto;
            align-items: center;
            justify-content: center;
            width: 189px;
            height: 42px;
            transition: 300ms;
            border: 1px solid var(--g_color_table_border_gray);
            color: var(--color_primary_link);
            font-size: 14px;
            font-weight: bold;
            line-height: 2.286;

            &_icon {
              color: var(--color_primary_link);
            }

            &:hover {
              transform: scale3d(1.1, 1.1, 1);
            }
          }
        }
        */
        &_link {
          display: flex;
          justify-content: flex-start;
          margin-top: 10px;
          margin-bottom: 20px;

          @include responsive.mq_down(sp) {
            flex-wrap: wrap;
            justify-content: center;
          }

          &_button {
            display: flex;
            flex: 0 0 auto;
            align-items: center;
            justify-content: center;
            width: 189px;
            height: 42px;
            transition: 300ms;
            border: 1px solid var(--g_color_table_border_gray);
            text-decoration: none;

            &:hover {
              opacity: 0.7;
            }

            @include responsive.mq_down(sp) {
              margin: 0 6px 12px;
            }

            // &:hover {
            //   transform: scale3d(1.1, 1.1, 1);
            // }

            &_icon {
              color: var(--color_primary_link);
              font-weight: bold;
            }

            &_text {
              color: var(--color_primary_link);
              font-size: 14px;
              font-weight: bold;
              line-height: 2.286;
            }
          }
        }
      }
    }
  }

  &_link {
    color: var(--color_primary_link);
  }

  &_contentDetail {
    margin-bottom: 60px;
    color: var(--color_text_base);
    line-height: 28px;

    :deep(iframe) {
      width: 100% !important;
    }

    &_text {
      // transform: rotate(0.03deg);
      // font-family: var(--g_font_family_MPuls);
      font-family: var(--g_font_family_NotoSansCJKjp);
      font-size: 16px;
      line-height: 1.8;
    }
  }

  &_contentNote {
    margin: 20px 0;
    color: var(--g_color_text_f2423e);
    font-size: 19px;
    line-height: 28px;
  }

  &_accountReturn {
    display: inline-block;
    padding: 8px 15px 10px;
    transition: .3s;
    background-color: #014da3;
    color: var(--color_primary_link);
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
      opacity: 0.7;
    }
  }
}
</style>
